// Tüm görsel yüklemelerinin tek kapısı.
//
// SORUN: panelde on ayrı ekran kendi yükleme kodunu taşıyordu ve her biri
// farklı bir yerde tökezliyordu:
//
//   * Paylaşılan API istemcisinin varsayılan zaman aşımı 15 saniye. Birkaç
//     megabaytlık bir fotoğrafı sahadaki mobil bağlantıdan göndermek bunun çok
//     üstünde sürüyor; istek yarıda kesilip "yüklenemedi" diyordu. Yükleme
//     isteğinin zaman aşımı ayrı olmalı.
//   * Bazı ekranlar dosyayı hiç küçültmeden gönderiyordu (takım logosu, oyuncu
//     fotoğrafı, Add_Player/Update_Player/Add_Teams/Update_Team) — telefon
//     fotoğrafı sunucu sınırına takılıyordu.
//   * Yanıttaki adres her uçta farklı anahtarda geliyor (`photoUrl`, `logo`,
//     `matchPictureUrl`, `url`). Tek bir anahtara bakan ekranlar adresi
//     okuyamıyor, yüklenen görseli forma yazamıyordu.
//   * Hata mesajı çoğu yerde "Bir hata oluştu" idi; sunucunun anlattığı sebep
//     (boyut, tür, yetki) kullanıcıya hiç ulaşmıyordu.
//
// Burada hepsi tek yerde çözülür. Yeni bir ekran yalnızca `uploadImage`
// çağırır.

import axios from "axios";
import api from "../api/api";
import { API_TIMEOUTS } from "../api/apiConfig";
import { ImagePrepareError, prepareImageForUpload } from "../utils/imageUpload";

/** Yanıt gövdesinde adresin bulunabileceği anahtarlar (uçtan uca farklı). */
const URL_KEYS = [
  "url",
  "photoUrl",
  "logo",
  "league_logo",
  "matchPictureUrl",
  "match_picture",
  "player_img",
  "image_url",
  "location",
  "path",
];

const str = (value) => (value == null ? "" : String(value).trim());

/**
 * Yanıt gövdesinden yüklenen görselin adresini çıkarır.
 *
 * Gövde düz ya da `{ data: {...} }` / `{ change: { payload: {...} } }` sarmalı
 * olabilir (logo değişikliği onaya düştüğünde ikincisi geliyor).
 *
 * @param {any} body
 * @returns {string}
 */
export const readUploadedUrl = (body) => {
  const candidates = [body, body?.data, body?.change?.payload, body?.request, body?.file];
  for (const source of candidates) {
    if (!source || typeof source !== "object") continue;
    for (const key of URL_KEYS) {
      const value = str(source[key]);
      if (value) return value;
    }
  }
  return "";
};

/**
 * Yükleme hatasını kullanıcıya gösterilecek Türkçe metne çevirir.
 *
 * Sunucu zaten anlaşılır bir mesaj döndürüyorsa o kullanılır; ağ katmanında
 * kalan hatalar (zaman aşımı, bağlantı kopması) kendi metnini alır.
 *
 * @param {any} error
 * @param {string} [fallback]
 * @returns {string}
 */
export const describeUploadError = (error, fallback = "Görsel yüklenemedi.") => {
  if (error instanceof ImagePrepareError) return error.message;

  const status = error?.response?.status;
  const serverMessage = str(error?.response?.data?.message) || str(error?.response?.data?.error);
  if (serverMessage) return serverMessage;

  if (error?.code === "ECONNABORTED") {
    return "Yükleme zaman aşımına uğradı. Bağlantınız yavaş olabilir; daha küçük bir fotoğrafla tekrar deneyin.";
  }
  if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") return "Yükleme iptal edildi.";
  if (status === 401) return "Oturumunuz sona ermiş görünüyor. Tekrar giriş yapıp deneyin.";
  if (status === 403) return "Bu görseli yükleme yetkiniz yok.";
  if (status === 404) return "Yükleme adresi bulunamadı. Sayfayı yenileyip tekrar deneyin.";
  if (status === 413) return "Görsel sunucunun kabul ettiğinden büyük. Daha küçük bir fotoğraf seçin.";
  if (!error?.response) return "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.";
  return fallback;
};

/**
 * Görsel taşıyan bir multipart isteğinin axios ayarları.
 *
 * Kayıt oluşturma akışları (yeni oyuncu, yeni takım) görseli diğer alanlarla
 * birlikte gönderdiği için `uploadImage` yerine kendi isteklerini kurar; zaman
 * aşımının orada da uzun olması gerekir.
 *
 * @param {{ onProgress?: (percent: number) => void, signal?: AbortSignal }} [options]
 * @returns {import("axios").AxiosRequestConfig}
 */
export const uploadRequestConfig = ({ onProgress, signal } = {}) => {
  // Content-Type BİLEREK verilmiyor: sınır (boundary) dizesini tarayıcı üretir.
  const config = { timeout: API_TIMEOUTS.UPLOAD };
  if (signal) config.signal = signal;
  if (onProgress) {
    config.onUploadProgress = (event) => {
      if (!event?.total) return;
      onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
    };
  }
  return config;
};

/**
 * Bir görseli hazırlar ve yükler.
 *
 * @param {object} options
 * @param {string} options.path      Yükleme ucu (ör. `/oyuncular/updateImage/12`)
 * @param {string} options.field     Multipart alan adı (ör. `player_img`)
 * @param {File}   options.file      Kullanıcının seçtiği dosya
 * @param {"post"|"patch"|"put"} [options.method]
 * @param {import("axios").AxiosInstance} [options.client] Varsayılan: paylaşılan API istemcisi
 * @param {Record<string, string>} [options.fields] Forma eklenecek ek alanlar
 * @param {(percent: number) => void} [options.onProgress] 0-100 arası ilerleme
 * @param {AbortSignal} [options.signal]
 * @param {{ budget?: number, maxEdge?: number }} [options.prepare] Küçültme ayarı
 *   (ör. 1920 × 1080 yayın grafikleri için daha büyük `maxEdge`)
 * @returns {Promise<{ url: string, data: any }>}
 */
export const uploadImage = async ({
  path,
  field,
  file,
  method = "patch",
  client = api,
  fields = null,
  onProgress = null,
  signal = null,
  prepare = undefined,
}) => {
  if (!file) throw new ImagePrepareError("Bir görsel dosyası seçin.", "NO_FILE");

  const ready = await prepareImageForUpload(file, prepare);

  const form = new FormData();
  form.append(field, ready);
  if (fields) {
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) form.append(key, value);
    });
  }

  // Yükleme isteği okuma isteğinden çok daha uzun sürer; paylaşılan 15 sn'lik
  // varsayılan zaman aşımı burada isteği yarıda keserdi.
  const response = await client[method](path, form, uploadRequestConfig({ onProgress, signal }));
  const data = response?.data;
  return { url: readUploadedUrl(data), data };
};

export default uploadImage;
